/**
 * Workout Type Pie Chart Component
 *
 * Simple CSS-based pie chart showing workout type distribution
 * Mobile: tap to show percentage, 2-column legend layout
 */

import { useState } from 'react';
import './WorkoutTypePieChart.css';

interface PieChartData {
  label: string;
  value: number;
  color: string;
}

interface WorkoutTypePieChartProps {
  data: PieChartData[];
}

export function WorkoutTypePieChart({ data }: WorkoutTypePieChartProps): JSX.Element {
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const total = data.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return (
      <div className="pie-chart pie-chart--empty">
        <div className="pie-chart__empty-icon">📊</div>
        <p className="pie-chart__empty-text">No workouts this month</p>
      </div>
    );
  }

  // Calculate conic gradient stops
  let currentAngle = 0;
  const gradientStops = data.map((item) => {
    const startAngle = currentAngle;
    const sliceAngle = (item.value / total) * 360;
    currentAngle += sliceAngle;
    return `${item.color} ${startAngle}deg ${currentAngle}deg`;
  });

  const gradientStyle = {
    background: `conic-gradient(${gradientStops.join(', ')})`,
  };

  const handleItemClick = (label: string): void => {
    setActiveItem(activeItem === label ? null : label);
  };

  return (
    <div className="pie-chart">
      <div className="pie-chart__container">
        <div className="pie-chart__chart" style={gradientStyle}>
          <div className="pie-chart__center">
            <span className="pie-chart__total">{total}</span>
            <span className="pie-chart__total-label">workouts</span>
          </div>
        </div>
      </div>

      <div className="pie-chart__legend">
        {data.map((item) => {
          const percentage = Math.round((item.value / total) * 100);
          const isActive = activeItem === item.label;
          return (
            <div
              key={item.label}
              className={`pie-chart__legend-item ${isActive ? 'pie-chart__legend-item--active' : ''}`}
              onClick={() => handleItemClick(item.label)}
            >
              <span
                className="pie-chart__legend-color"
                style={{ backgroundColor: item.color }}
              />
              <span className="pie-chart__legend-label">{item.label}</span>
              <span className="pie-chart__legend-value">
                {item.value} ({percentage}%)
              </span>
              <span className="pie-chart__legend-value--mobile">
                {isActive ? `${percentage}%` : item.value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
